import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSignature, Download, Upload } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import type { ContractDto, ContractDurationOption, ContractSlaLevel } from "@finora/types";
import { api } from "../../lib/api";

const DURATION_LABELS: Record<ContractDurationOption, string> = {
  UNDEFINED: "Neodređeno",
  MONTHS_12: "12 meseci",
  MONTHS_24: "24 meseca",
};
const SLA_LABELS: Record<ContractSlaLevel, string> = {
  STANDARD: "Standard",
  PREMIUM: "Premium",
};
const STATUS_LABELS: Record<ContractDto["status"], { label: string; className: string }> = {
  AWAITING_UPLOAD: { label: "Čeka upload potpisanog primerka", className: "bg-amber-100 text-amber-700" },
  PENDING_APPROVAL: { label: "Čeka pregled HAFiskal administratora", className: "bg-sky-100 text-sky-700" },
  APPROVED: { label: "Odobren", className: "bg-emerald-100 text-emerald-700" },
  REJECTED: { label: "Odbijen - pogledajte razlog ispod", className: "bg-red-100 text-red-700" },
};

/**
 * "Moj ugovor" - tenant-facing stranica (dodato 01.10.2026, vidi razgovor
 * "admin placeholderi ugovora"). Firma ovde vidi SVOJ prihvaćeni ugovor
 * (PDF, status, uploadovane dokumente) - dosad je ovo postojalo samo na
 * strani admina (profil firme -> "Ugovor" tab); API rute (`/company/contracts*`)
 * su već postojale, samo stranica u frontend-u nije bila napravljena.
 * Ako firma još nije prihvatila ugovor (npr. onboarding nije stigao do tog
 * koraka), lista je prazna - prihvatanje se i dalje radi kroz onboarding
 * wizard, ne ovde.
 */
export function MyContractPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { data: contracts, isLoading } = useQuery({
    queryKey: ["my-contracts"],
    queryFn: () => api.getMyContracts(),
  });

  const upload = useMutation({
    mutationFn: ({ contractId, files }: { contractId: string; files: File[] }) =>
      api.uploadContractDocuments(contractId, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-contracts"] });
      setUploadError(null);
    },
    onError: (err) => {
      setUploadError(err instanceof ApiError ? err.response.message : "Upload potpisanog ugovora nije uspeo.");
    },
    onSettled: () => setUploadingFor(null),
  });

  const sorted = [...(contracts ?? [])].sort(
    (a, b) => new Date(b.acceptedAt).getTime() - new Date(a.acceptedAt).getTime(),
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Moj ugovor</h1>
        <p className="text-sm text-slate-500">
          Ugovor o korišćenju HAFiskal ESIR usluge koji ste prihvatili, status pregleda i potpisani primerak.
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length > 0 && uploadingFor) {
            upload.mutate({ contractId: uploadingFor, files });
          }
          e.target.value = "";
        }}
      />

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-32 w-full" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="rounded-xl border border-border bg-surface p-8 shadow-subtle">
          <EmptyState
            icon={FileSignature}
            title="Nemate još prihvaćen ugovor"
            description="Ugovor o korišćenju usluge se prihvata u toku registracije/onboardinga."
          />
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map((contract) => {
            const status = STATUS_LABELS[contract.status];
            return (
              <div key={contract.id} className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.className}`}>
                    {status.label}
                  </span>
                  <span className="text-sm font-medium text-slate-800">
                    {DURATION_LABELS[contract.durationOption]} · {SLA_LABELS[contract.slaLevel]}
                  </span>
                  <a
                    href={api.getContractPdfUrl(contract.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto flex items-center gap-1 text-xs font-medium text-brand hover:underline"
                  >
                    <Download className="h-3.5 w-3.5" aria-hidden />
                    Preuzmi PDF ugovora
                  </a>
                </div>

                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-500 sm:grid-cols-3">
                  <span>Prihvaćen: {new Date(contract.acceptedAt).toLocaleDateString("sr-RS")}</span>
                  {contract.status === "AWAITING_UPLOAD" && (
                    <span>Rok za upload: {new Date(contract.uploadDeadlineAt).toLocaleDateString("sr-RS")}</span>
                  )}
                </div>

                {contract.status === "REJECTED" && contract.rejectionReason && (
                  <p className="mt-2 rounded-lg bg-red-50 px-2 py-1.5 text-xs text-red-700">
                    Razlog odbijanja: {contract.rejectionReason}
                  </p>
                )}

                <div className="mt-3 border-t border-border pt-3">
                  <p className="mb-1.5 text-xs font-medium text-slate-500">Potpisan primerak</p>
                  {contract.documents.length === 0 ? (
                    <p className="mb-2 text-xs text-slate-400">Još niste priložili potpisan primerak ugovora.</p>
                  ) : (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {contract.documents.map((d) => (
                        <span
                          key={d.id}
                          className="flex items-center gap-1 rounded-lg border border-border bg-app-bg px-2 py-1 text-xs text-slate-600"
                        >
                          {d.fileName}
                        </span>
                      ))}
                    </div>
                  )}
                  {(contract.status === "AWAITING_UPLOAD" || contract.status === "REJECTED") && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={upload.isPending && uploadingFor === contract.id}
                      onClick={() => {
                        setUploadingFor(contract.id);
                        fileInputRef.current?.click();
                      }}
                    >
                      <Upload className="h-3.5 w-3.5" aria-hidden />
                      {upload.isPending && uploadingFor === contract.id ? "Učitavam..." : "Priloži potpisan primerak"}
                    </Button>
                  )}
                </div>

                {uploadError && uploadingFor === null && (
                  <p className="mt-2 rounded-lg bg-red-50 px-2 py-1.5 text-xs text-red-700">{uploadError}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
